import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useSWR, { useSWRConfig } from "swr";
import axios from "axios";
import { readStaleSessionCache, writeSessionCache } from "@/lib/sessionCache";
import { SkeletonTable } from "@/components/Skeletons";
import PageLayout from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Plus, Search, Users, CheckCircle, XCircle, Clock, Edit, Trash2, CalendarIcon, Trophy, Skull, TrendingUp, UserCheck, AlertTriangle, FileSpreadsheet, BarChart3, Ghost, ChevronDown, ChevronUp } from "lucide-react";
import { it } from "date-fns/locale";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { convertToItalianFormat } from "@/utils/dateUtils";
import { useYear } from "@/contexts/YearContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stati disponibili (limitati ai primi 5 per uso in dropdown)
const STATI = [
  "Da Elaborare",
  "In Elaborazione",
  "Elaborato",
  "Preso App in Showroom",
  "Consegnato"
];

// Stati per le tab di filtro
const STATI_FILTRO = [
  { value: "all", label: "Tutti gli Stati" },
  { value: "Da Elaborare", label: "Da Elaborare" },
  { value: "In Elaborazione", label: "In Elaborazione" },
  { value: "Elaborato", label: "Elaborato" },
  { value: "Preso App in Showroom", label: "Preso App in Showroom" },
  { value: "Consegnato", label: "Consegnato" },
  { value: "consegnati_inesitati", label: "Consegnati Inesitati" },
  { value: "inconcludenti", label: "Inconcludenti" }
];

// Stati che rientrano nel filtro "Inconcludenti"
const STATI_INCONCLUDENTI = [
  "Non Risposto da Richiamare",
  "Non Risposto 2° Tentativo",
  "Non Risposto 3° Tentativo",
  "In Attesa Riscontri Cliente",
  "Sparito",
  "Perditempo"
];

// Helper per calcolare i giorni dalla consegna
const getGiorniDaConsegna = (dataConsegnato) => {
  if (!dataConsegnato) return 0;
  const oggi = new Date();
  const dataConsegna = new Date(dataConsegnato);
  const diffTime = oggi - dataConsegna;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Commerciali disponibili
const COMMERCIALI = ["Commerciale 1", "Commerciale 2", "Mario"];

// Priorità disponibili
const PRIORITA_OPTIONS = [
  { value: "Urgente 1 g", label: "Urgente 1 g" },
  { value: "Ordinario 3-5 gg", label: "Ordinario 3-5 gg" },
  { value: "Lento 6-10 gg", label: "Lento 6-10 gg" }
];

// Categorie merceologiche
const CATEGORIE_MERCEOLOGICHE = [
  "Infissi",
  "Porte",
  "Infissi e Porte",
  "Persiane",
  "Blindati",
  "Altro vedi Nota"
];

// Stati validi per valorizzare l'esito
const STATI_VALIDI_PER_ESITO = ["Consegnato", "Perditempo", "Sparito"];

const getStatusColor = (stato) => {
  switch (stato) {
    case "Elaborato":
    case "Consegnato":
      return "status-green";
    case "Preso App in Showroom":
      return "status-blue";
    case "Perditempo":
    case "Sparito":
      return "status-red";
    case "Fuori Budget":
      return "status-purple";
    case "In Elaborazione":
    case "In Attesa Riscontri Cliente":
      return "status-yellow";
    case "Attesa Uff. Tecnico":
      return "status-orange";
    default:
      return "status-gray";
  }
};

// Helper per convertire data stringa gg-mm-aaaa in Date object
const parseItalianDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split('-');
  if (parts.length === 3 && parts[0].length === 2) {
    return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
  }
  if (parts.length === 3 && parts[0].length === 4) {
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  }
  return null;
};

// Helper per formattare Date object in gg-mm-aaaa
const formatItalianDate = (date) => {
  if (!date) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export default function InterviewsPage({ user, logout }) {
  const { selectedYear } = useYear();
  const _cached = readStaleSessionCache(`interviews_${selectedYear}`);
  const { mutate } = useSWRConfig();

  // SWR keys
  const interviewsKey = `/interviews?year=${selectedYear}&light=true`;
  const statsKey = `/interviews/stats?year=${selectedYear}`;
  const urgentKey = `/interviews/urgent?year=${selectedYear}`;
  const monthlyKey = `/interviews/monthly-stats?year=${selectedYear}`;

  const { data: swrInterviews } = useSWR(interviewsKey, { fallbackData: _cached?.interviews });
  const { data: swrStats } = useSWR(statsKey, { fallbackData: _cached?.stats });
  const { data: swrUrgent } = useSWR(urgentKey, { fallbackData: _cached?.urgentData });
  const { data: swrMonthly } = useSWR(monthlyKey, { fallbackData: _cached?.monthlyStats });

  const [interviews, setInterviews] = useState(swrInterviews || _cached?.interviews || []);
  const [stats, setStats] = useState(swrStats || _cached?.stats || {
    totale: 0, vinti: 0, persi: 0, spariti: 0,
    percentuale_vinti: 0, consegnati: 0,
    commerciali: { "Commerciale 1": 0, "Commerciale 2": 0, "Mario": 0 }
  });
  const [urgentData, setUrgentData] = useState(swrUrgent || _cached?.urgentData || { warning: [], critical: [] });
  const [monthlyStats, setMonthlyStats] = useState(swrMonthly || _cached?.monthlyStats || []);
  const [chartView, setChartView] = useState("total");
  const [showReports, setShowReports] = useState(false);
  const [showCriticalAlerts, setShowCriticalAlerts] = useState(true);
  const [loading, setLoading] = useState(!_cached && !swrInterviews);
  const [searchTerm, setSearchTerm] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  
  // Precompila la ricerca dalla query string ?search=... (usato dai link della pagina Follow UP)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("search");
    if (q !== null) setSearchTerm(q);
  }, [location.search]);
  
  const clearSearchFilter = () => {
    setSearchTerm("");
    if (location.search) navigate(location.pathname, { replace: true });
  };
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoriaFilter, setCategoriaFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingInterview, setEditingInterview] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    citta: "",
    via: "",
    civico: "",
    piano: "",
    note: "",
    data_intervista: "",
    commerciale: "",
    categoria_merceologica: "",
    priorita: "Ordinario 3-5 gg"
  });
  const [currentTime, setCurrentTime] = useState(new Date());

  // Sync SWR → local state (per compatibilità con la logica di mutation esistente)
  useEffect(() => { if (swrInterviews) setInterviews(swrInterviews); }, [swrInterviews]);
  useEffect(() => { if (swrStats) setStats(swrStats); }, [swrStats]);
  useEffect(() => { if (swrUrgent) setUrgentData(swrUrgent); }, [swrUrgent]);
  useEffect(() => { if (swrMonthly) setMonthlyStats(swrMonthly); }, [swrMonthly]);
  useEffect(() => { if (swrInterviews && loading) setLoading(false); }, [swrInterviews, loading]);

  const fetchData = async () => {
    // Post-mutazione: invalida tutti gli endpoint SWR (rifetch da server)
    try {
      await Promise.all([mutate(interviewsKey), mutate(statsKey), mutate(urgentKey), mutate(monthlyKey)]);
      writeSessionCache(`interviews_${selectedYear}`, { interviews, stats, urgentData, monthlyStats });
    } catch (e) {
      console.error("Error refreshing interviews:", e);
    }
  };

  useEffect(() => {
    // Nessun fetch qui: SWR gestisce automaticamente il fetch iniziale quando cambia selectedYear (cambia la key)
  }, [selectedYear]);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.data_intervista) {
      toast.error("Seleziona una data di intervista");
      return;
    }
    if (!formData.commerciale) {
      toast.error("Seleziona un commerciale");
      return;
    }
    if (!formData.categoria_merceologica) {
      toast.error("Seleziona una categoria merceologica");
      return;
    }
    
    try {
      if (editingInterview) {
        await axios.put(`${API}/interviews/${editingInterview.id}`, formData, { withCredentials: true });
        toast.success("Intervista aggiornata");
      } else {
        await axios.post(`${API}/interviews`, formData, { withCredentials: true });
        toast.success("Intervista creata");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (e) {
      console.error("Error saving interview:", e);
      toast.error("Errore nel salvataggio");
    }
  };

  const handleAggiungiAlFunnel = async () => {
    // Validazione campi obbligatori per il Funnel
    if (!formData.nome || !formData.cognome || !formData.email || !formData.telefono) {
      toast.error("Compila nome, cognome, email e telefono prima di inviare");
      return;
    }
    
    try {
      await axios.post(`${API}/send-to-funnel`, {
        nome: formData.nome,
        cognome: formData.cognome,
        email: formData.email,
        telefono: formData.telefono,
        data: new Date().toLocaleDateString('it-IT')
      }, { withCredentials: true });
      toast.success("Cliente aggiunto al Funnel!");
    } catch (e) {
      console.error("Error adding to funnel:", e);
      toast.error("Errore nell'aggiunta al Funnel");
    }
  };

  const handleCopiaBackupInterviste = async () => {
    // Validazione campi obbligatori per il Backup
    if (!formData.nome || !formData.cognome) {
      toast.error("Compila almeno nome e cognome prima di copiare");
      return;
    }
    
    try {
      await axios.post(`${API}/send-to-backup-interviste`, {
        nome: formData.nome,
        cognome: formData.cognome,
        email: formData.email || "",
        telefono: formData.telefono || "",
        citta: formData.citta || "",
        via: formData.via || "",
        civico: formData.civico || "",
        piano: formData.piano || "",
        data_intervista: formData.data_intervista || "",
        note: formData.note || ""
      }, { withCredentials: true });
      toast.success("Dati copiati su Google Sheets!");
    } catch (e) {
      console.error("Error copying to Google Sheets:", e);
      toast.error("Errore nella copia su Google Sheets");
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      // Prepara i dati da aggiornare
      const updateData = { stato: newStatus };
      
      // Imposta automaticamente l'esito in base allo stato selezionato (minuscolo per coerenza con il frontend)
      if (newStatus === "Fuori Budget" || newStatus === "Perditempo") {
        updateData.esito = "perso";
      } else if (newStatus === "Sparito") {
        updateData.esito = "sparito";
      }
      
      await axios.put(`${API}/interviews/${id}`, updateData, { withCredentials: true });
      
      // Mostra messaggi appropriati
      if (updateData.esito) {
        const esitoDisplay = updateData.esito === "perso" ? "Perso" : "Sparito";
        toast.success(`Stato aggiornato ed esito impostato automaticamente: ${esitoDisplay}`);
      } else {
        toast.success("Stato aggiornato");
      }
      
      // Mostra avviso speciale per "Attesa Uff. Tecnico"
      if (newStatus === "Attesa Uff. Tecnico") {
        toast.warning("⚠️ Attenzione: aggiungi nelle Note una descrizione del problema", {
          duration: 6000
        });
      }
      
      fetchData();
    } catch (e) {
      console.error("Error updating status:", e);
      toast.error("Errore nell'aggiornamento");
    }
  };

  const [clickTimeout, setClickTimeout] = useState(null);

  const handleEsitoClick = (id, esito, currentEsito) => {
    // Se c'è già un timeout pendente, è un doppio clic
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      // Doppio clic: rimuovi l'esito solo se è già selezionato
      if (currentEsito === esito) {
        handleEsitoChange(id, null);
      }
      return;
    }
    
    // Imposta un timeout per il clic singolo
    const timeout = setTimeout(() => {
      setClickTimeout(null);
      // Clic singolo: imposta l'esito (solo se diverso da quello attuale)
      if (currentEsito !== esito) {
        handleEsitoChange(id, esito);
      }
    }, 250); // 250ms per distinguere doppio clic
    
    setClickTimeout(timeout);
  };

  const handleEsitoChange = async (id, esito) => {
    // Trova l'intervista per verificare lo stato
    const interview = interviews.find(i => i.id === id);
    if (!interview) return;
    
    // Se esito è null, stiamo rimuovendo l'esito (doppio clic)
    if (esito === null) {
      try {
        await axios.put(`${API}/interviews/${id}`, { esito: "" }, { withCredentials: true });
        toast.success("Esito rimosso");
        fetchData();
      } catch (e) {
        console.error("Error removing esito:", e);
        toast.error("Errore nella rimozione dell'esito");
      }
      return;
    }
    
    // Verifica che lo stato sia Consegnato, Perditempo o Sparito
    if (!STATI_VALIDI_PER_ESITO.includes(interview.stato)) {
      toast.error("Attenzione: Prima di valorizzare l'esito, seleziona lo stato 'Consegnato', 'Perditempo' o 'Sparito'");
      return;
    }
    
    try {
      await axios.put(`${API}/interviews/${id}`, { esito }, { withCredentials: true });
      const message = esito === "vinto" ? "Intervista vinta!" : 
                     esito === "perso" ? "Intervista persa" : 
                     "Cliente sparito";
      toast.success(message);
      fetchData();
    } catch (e) {
      console.error("Error updating esito:", e);
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa intervista?")) return;
    try {
      await axios.delete(`${API}/interviews/${id}`, { withCredentials: true });
      toast.success("Intervista eliminata");
      fetchData();
    } catch (e) {
      console.error("Error deleting interview:", e);
      toast.error("Errore nell'eliminazione");
    }
  };

  const openEditDialog = (interview) => {
    setEditingInterview(interview);
    const parsedDate = parseItalianDate(interview.data_intervista);
    setSelectedDate(parsedDate);
    setFormData({
      nome: interview.nome,
      cognome: interview.cognome,
      email: interview.email || "",
      telefono: interview.telefono,
      citta: interview.citta,
      via: interview.via,
      civico: interview.civico,
      piano: interview.piano || "",
      note: interview.note || "",
      data_intervista: interview.data_intervista,
      commerciale: interview.commerciale,
      categoria_merceologica: interview.categoria_merceologica || "",
      priorita: interview.priorita || "Ordinario 3-5 gg"
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingInterview(null);
    setSelectedDate(null);
    setFormData({
      nome: "",
      cognome: "",
      email: "",
      telefono: "",
      citta: "",
      via: "",
      civico: "",
      piano: "",
      note: "",
      data_intervista: "",
      commerciale: "",
      categoria_merceologica: "",
      priorita: "Ordinario 3-5 gg"
    });
  };

  const filteredInterviews = interviews.filter((i) => {
    // Filtro per ricerca
    const matchesSearch = `${i.nome} ${i.cognome} ${i.codice} ${i.telefono || ''} ${i.priorita || ''}`.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtro per stato
    let matchesStatus = true;
    if (statusFilter === "all") {
      matchesStatus = true;
    } else if (statusFilter === "inconcludenti") {
      matchesStatus = STATI_INCONCLUDENTI.includes(i.stato);
    } else if (statusFilter === "consegnati_inesitati") {
      matchesStatus = i.stato === "Consegnato" && !i.esito;
    } else {
      matchesStatus = i.stato === statusFilter;
    }
    
    // Filtro per categoria merceologica (marketing)
    let matchesCategoria = true;
    if (categoriaFilter !== "all") {
      const cat = i.categoria_merceologica || "";
      const stato = i.stato || "";
      if (categoriaFilter === "solo_porte") {
        matchesCategoria = cat === "Porte";
      } else if (categoriaFilter === "porte_e_infissi") {
        matchesCategoria = cat === "Infissi e Porte";
      } else if (categoriaFilter === "solo_infissi") {
        matchesCategoria = cat === "Infissi";
      } else if (categoriaFilter === "fuori_budget") {
        matchesCategoria = stato === "Fuori Budget";
      }
    }
    
    return matchesSearch && matchesStatus && matchesCategoria;
  });

  // Calcola conteggi per ogni stato (per le tab)
  const statusCounts = {
    all: interviews.length,
    "Da Elaborare": interviews.filter(i => i.stato === "Da Elaborare").length,
    "In Elaborazione": interviews.filter(i => i.stato === "In Elaborazione").length,
    "Elaborato": interviews.filter(i => i.stato === "Elaborato").length,
    "Preso App in Showroom": interviews.filter(i => i.stato === "Preso App in Showroom").length,
    "Consegnato": interviews.filter(i => i.stato === "Consegnato").length,
    "consegnati_inesitati": interviews.filter(i => i.stato === "Consegnato" && !i.esito).length,
    "inconcludenti": interviews.filter(i => STATI_INCONCLUDENTI.includes(i.stato)).length
  };

  // Clienti urgenti non gestiti (priorità "Urgente 1 g", giorni > 1, stato Da Elaborare o In Elaborazione)
  const clientiUrgentiNonGestiti = interviews.filter(i => 
    i.priorita === "Urgente 1 g" && 
    i.days_since_interview > 1 && 
    (i.stato === "Da Elaborare" || i.stato === "In Elaborazione")
  );

  const getRowStyle = (interview) => {
    if (interview.esito === "vinto") return { backgroundColor: "#d1fae5" }; // emerald-100
    if (interview.esito === "perso") return { backgroundColor: "#ede9fe" }; // violet-100
    if (interview.esito === "sparito") return { backgroundColor: "#f1f5f9" }; // slate-100
    
    // Consegnati inesitati - colore in base ai giorni
    if (interview.stato === "Consegnato" && !interview.esito) {
      const giorniDaConsegna = getGiorniDaConsegna(interview.data_consegnato);
      if (giorniDaConsegna >= 7) return { backgroundColor: "#ede9fe" }; // viola - violet-100
      if (giorniDaConsegna >= 5) return { backgroundColor: "#ffedd5" }; // arancione - orange-100
    }
    
    const isWaiting = interview.stato === "Da Elaborare" || interview.stato === "In Elaborazione";
    if (isWaiting && interview.days_since_interview > 5) return { backgroundColor: "#fee2e2" }; // red-100
    if (isWaiting && interview.days_since_interview > 3) return { backgroundColor: "#ffe4e6" }; // rose-100
    return {};
  };

  const getRowClass = (interview) => {
    if (interview.esito === "vinto") return "row-vinto";
    if (interview.esito === "perso") return "row-perso";
    if (interview.esito === "sparito") return "row-sparito";
    
    // Consegnati inesitati - classe in base ai giorni
    if (interview.stato === "Consegnato" && !interview.esito) {
      const giorniDaConsegna = getGiorniDaConsegna(interview.data_consegnato);
      if (giorniDaConsegna >= 7) return "row-inesitato-viola";
      if (giorniDaConsegna >= 5) return "row-inesitato-arancio";
    }
    
    const isWaiting = interview.stato === "Da Elaborare" || interview.stato === "In Elaborazione";
    if (isWaiting && interview.days_since_interview > 5) return "row-critical";
    if (isWaiting && interview.days_since_interview > 3) return "row-warning";
    return "";
  };

  return (
    <PageLayout user={user} logout={logout} testId="interviews-page">
        <div className="flex-1 flex flex-col overflow-hidden p-4 lg:p-6 xl:p-8">
          {/* Header - Compatto su schermi bassi */}
          <div className="flex items-center justify-between mb-4 lg:mb-6 flex-shrink-0">
            <div>
              <h1 className="text-xl lg:text-2xl xl:text-3xl font-bold text-slate-900">Interviste Clienti</h1>
              <p className="text-slate-600 text-sm hidden lg:block">Gestisci le consulenze dello showroom</p>
            </div>
            <div className="flex items-center gap-2 lg:gap-4">
              {/* Box Data e Ora - Nascosto su schermi piccoli */}
              <div className="hidden xl:flex bg-white border border-slate-200 rounded-lg px-3 py-1.5 shadow-sm">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-slate-500" />
                  <span className="text-xs font-medium text-slate-700">
                    {currentTime.toLocaleDateString('it-IT', { 
                      weekday: 'short', 
                      day: 'numeric', 
                      month: 'short'
                    })}
                  </span>
                  <span className="text-slate-400">|</span>
                  <Clock className="w-4 h-4 text-slate-500" />
                  <span className="text-xs font-medium text-slate-700">
                    {currentTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
              <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white text-sm" data-testid="add-interview-btn">
                  <Plus className="w-4 h-4 mr-1 lg:mr-2" />
                  <span className="hidden sm:inline">Nuova Intervista</span>
                  <span className="sm:hidden">Nuovo</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingInterview ? "Modifica Intervista" : "Nuova Intervista"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nome">Nome *</Label>
                      <Input
                        id="nome"
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                        required
                        data-testid="input-nome"
                      />
                    </div>
                    <div>
                      <Label htmlFor="cognome">Cognome *</Label>
                      <Input
                        id="cognome"
                        value={formData.cognome}
                        onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                        required
                        data-testid="input-cognome"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="telefono">Telefono *</Label>
                      <Input
                        id="telefono"
                        value={formData.telefono}
                        onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                        required
                        data-testid="input-telefono"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="citta">Città *</Label>
                      <Input
                        id="citta"
                        value={formData.citta}
                        onChange={(e) => setFormData({ ...formData, citta: e.target.value })}
                        required
                        data-testid="input-citta"
                      />
                    </div>
                    <div>
                      <Label htmlFor="via">Via *</Label>
                      <Input
                        id="via"
                        value={formData.via}
                        onChange={(e) => setFormData({ ...formData, via: e.target.value })}
                        required
                        data-testid="input-via"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="civico">Civico *</Label>
                        <Input
                          id="civico"
                          value={formData.civico}
                          onChange={(e) => setFormData({ ...formData, civico: e.target.value })}
                          required
                          data-testid="input-civico"
                        />
                      </div>
                      <div>
                        <Label htmlFor="piano">Piano</Label>
                        <Input
                          id="piano"
                          value={formData.piano}
                          onChange={(e) => setFormData({ ...formData, piano: e.target.value })}
                          data-testid="input-piano"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Data Intervista *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal"
                            data-testid="input-data"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {selectedDate ? formatItalianDate(selectedDate) : "Seleziona data..."}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={(date) => {
                              setSelectedDate(date);
                              setFormData({ ...formData, data_intervista: formatItalianDate(date) });
                            }}
                            locale={it}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label>Commerciale *</Label>
                      <Select
                        value={formData.commerciale}
                        onValueChange={(value) => setFormData({ ...formData, commerciale: value })}
                      >
                        <SelectTrigger data-testid="input-commerciale">
                          <SelectValue placeholder="Seleziona commerciale..." />
                        </SelectTrigger>
                        <SelectContent>
                          {COMMERCIALI.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Categoria Merceologica *</Label>
                    <Select
                      value={formData.categoria_merceologica}
                      onValueChange={(value) => setFormData({ ...formData, categoria_merceologica: value })}
                    >
                      <SelectTrigger data-testid="input-categoria">
                        <SelectValue placeholder="Seleziona categoria..." />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIE_MERCEOLOGICHE.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priorità *</Label>
                    <Select
                      value={formData.priorita}
                      onValueChange={(value) => setFormData({ ...formData, priorita: value })}
                    >
                      <SelectTrigger data-testid="input-priorita">
                        <SelectValue placeholder="Seleziona priorità..." />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITA_OPTIONS.map((p) => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="note">Note</Label>
                    <Textarea
                      id="note"
                      value={formData.note}
                      onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                      rows={3}
                      data-testid="input-note"
                    />
                  </div>
                  <div className="flex flex-col gap-3 pt-4">
                    <div className="flex gap-2">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={handleAggiungiAlFunnel}
                        className="flex-1 text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                        data-testid="add-to-funnel-btn"
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Aggiungi al Funnel
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={handleCopiaBackupInterviste}
                        className="flex-1 text-blue-600 border-blue-300 hover:bg-blue-50"
                        data-testid="copy-to-sheets-btn"
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Copia su Google Sheets
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1">
                        Annulla
                      </Button>
                      <Button type="submit" className="flex-1 bg-[#1e40af] hover:bg-[#1e3a8a] text-white" data-testid="submit-interview-btn">
                        {editingInterview ? "Salva Modifiche" : "Crea Intervista"}
                      </Button>
                    </div>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          {/* Toggle Report e Statistiche */}
          <div className="mb-4">
            <Button
              onClick={() => setShowReports(!showReports)}
              variant="outline"
              className="w-full flex items-center justify-between bg-white hover:bg-slate-50 border-2"
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                <span className="font-semibold text-slate-900">Report e Statistiche</span>
              </div>
              <div className="flex items-center gap-2">
                {showReports ? (
                  <>
                    <span className="text-sm text-slate-500">Nascondi</span>
                    <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </>
                ) : (
                  <>
                    <span className="text-sm text-slate-500">Mostra</span>
                    <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </>
                )}
              </div>
            </Button>
          </div>

          {/* Alert Clienti Urgenti Non Gestiti - SEMPRE VISIBILE */}
          {clientiUrgentiNonGestiti.length > 0 && (
            <Card className="mb-4 border-red-300 bg-red-50">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-red-900 mb-2">
                      🚨 Attenzione: Cliente Urgente Non Gestito ({clientiUrgentiNonGestiti.length})
                    </h3>
                    <p className="text-xs text-red-700 mb-2">
                      Portarlo in stato Elaborato e procedere alla consegna!
                    </p>
                    <div className="space-y-1">
                      {clientiUrgentiNonGestiti.map((cliente) => (
                        <div key={cliente.id} className="text-sm text-red-800 bg-white rounded px-3 py-2 flex items-center justify-between">
                          <span className="font-medium">{cliente.nome} {cliente.cognome}</span>
                          <span className="text-red-600 text-xs">({cliente.days_since_interview} giorni in attesa)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sezione Report e Statistiche - Collassabile */}
          {showReports && (
            <div className="space-y-4 mb-8">
              {/* Stats Row 1 - Contatori principali */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="stat-card">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-slate-600" />
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.totale}</p>
                    <p className="text-xs text-slate-500">Totale</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Trophy className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">{stats.vinti}</p>
                    <p className="text-xs text-slate-500">Vinti</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Skull className="w-5 h-5 text-violet-600" />
                  <div>
                    <p className="text-2xl font-bold text-violet-600">{stats.persi}</p>
                    <p className="text-xs text-slate-500">Persi</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Ghost className="w-5 h-5 text-slate-600" />
                  <div>
                    <p className="text-2xl font-bold text-slate-600">{stats.spariti}</p>
                    <p className="text-xs text-slate-500">Spariti</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Alert Clienti senza esito da più di 5 giorni */}
          {stats.clienti_ritardo && stats.clienti_ritardo.length > 0 && (
            <Card className="mb-4 border-orange-200 bg-orange-50">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-orange-900 mb-2">
                      ⚠️ Clienti senza esito da più di 5 Giorni ({stats.clienti_ritardo.length})
                    </h3>
                    <div className="space-y-1">
                      {stats.clienti_ritardo.map((cliente) => (
                        <div key={cliente.id} className="text-sm text-orange-800 bg-white rounded px-3 py-2">
                          <span className="font-medium">{cliente.nome} {cliente.cognome}</span>
                          <span className="ml-2 text-orange-600">({cliente.giorni} giorni)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats Row 2 - Percentuale e Commerciali */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {/* Percentuale Vinti */}
            <Card className="stat-card">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-3xl font-bold text-emerald-600">{stats.percentuale_vinti}%</p>
                      <p className="text-xs text-slate-500">Percentuale Vinti su Consegnati ({stats.vinti}/{stats.consegnati})</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Interviste per Commerciale */}
            <Card className="stat-card">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3 mb-3">
                  <UserCheck className="w-5 h-5 text-blue-600" />
                  <p className="text-sm font-semibold text-slate-700">Interviste per Commerciale</p>
                </div>
                <div className="flex gap-4">
                  {COMMERCIALI.map((c) => (
                    <div key={c} className="text-center">
                      <p className="text-xl font-bold text-slate-900">{stats.commerciali?.[c] || 0}</p>
                      <p className="text-xs text-slate-500">{c}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Grafico Andamento Mensile */}
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  <CardTitle className="text-base">Andamento Mensile Interviste</CardTitle>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={chartView === "total" ? "default" : "outline"}
                    onClick={() => setChartView("total")}
                    className={chartView === "total" ? "bg-blue-600 hover:bg-blue-700" : ""}
                  >
                    Totali
                  </Button>
                  <Button
                    size="sm"
                    variant={chartView === "vinti" ? "default" : "outline"}
                    onClick={() => setChartView("vinti")}
                    className={chartView === "vinti" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                  >
                    Vinti
                  </Button>
                  <Button
                    size="sm"
                    variant={chartView === "persi" ? "default" : "outline"}
                    onClick={() => setChartView("persi")}
                    className={chartView === "persi" ? "bg-violet-600 hover:bg-violet-700" : ""}
                  >
                    Persi
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const [year, month] = value.split('-');
                      const monthNames = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
                      return `${monthNames[parseInt(month) - 1]} ${year}`;
                    }}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(value) => {
                      const [year, month] = value.split('-');
                      const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
                      return `${monthNames[parseInt(month) - 1]} ${year}`;
                    }}
                  />
                  <Legend />
                  {chartView === "total" && (
                    <Bar dataKey="total" fill="#3b82f6" name="Totale Interviste" />
                  )}
                  {chartView === "vinti" && (
                    <Bar dataKey="vinti" fill="#10b981" name="Clienti Vinti" />
                  )}
                  {chartView === "persi" && (
                    <Bar dataKey="persi" fill="#8b5cf6" name="Clienti Persi" />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
            </div>
          )}

          {/* Alert Critici - Oltre 5 giorni - Collapsabile */}
          {urgentData.critical?.length > 0 && (
            <Card className="mb-3 border-red-300 bg-red-50 flex-shrink-0" data-testid="critical-section">
              <CardHeader 
                className="pb-2 pt-3 cursor-pointer hover:bg-red-100/50 transition-colors"
                onClick={() => setShowCriticalAlerts(!showCriticalAlerts)}
              >
                <CardTitle className="text-sm font-semibold text-red-800 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600 animate-pulse" />
                    <span className="hidden sm:inline">Attenzione: Rischio di Perdita Cliente. In attesa da oltre 5 giorni.</span>
                    <span className="sm:hidden">Clienti critici ({urgentData.critical.length})</span>
                    <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">{urgentData.critical.length}</span>
                  </div>
                  {showCriticalAlerts ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </CardTitle>
              </CardHeader>
              {showCriticalAlerts && (
                <CardContent className="pt-0 pb-3">
                  <div className="flex flex-wrap gap-2">
                    {urgentData.critical.map((interview) => (
                      <div
                        key={interview.id}
                        className="flex items-center gap-1.5 bg-white px-2 py-1 rounded border border-red-400 shadow-sm text-sm"
                      >
                        <div className="w-2 h-2 bg-red-600 rounded-full animate-pulse" />
                        <span className="font-medium text-red-900">
                          {interview.nome} {interview.cognome}
                        </span>
                        <span className="text-xs font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                          {interview.days_since_interview}g
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Alert Warning - 3-5 giorni - Compatto */}
          {urgentData.warning?.length > 0 && (
            <Card className="mb-3 border-rose-200 bg-rose-50/50 flex-shrink-0" data-testid="urgent-section">
              <CardContent className="py-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 text-rose-800 text-sm font-medium">
                    <div className="w-2 h-2 bg-rose-500 rounded-full animate-pulse" />
                    <span className="hidden sm:inline">In attesa da 3+ giorni:</span>
                    <span className="sm:hidden">3+ giorni:</span>
                  </div>
                  {urgentData.warning.map((interview) => (
                    <span
                      key={interview.id}
                      className="text-xs bg-white px-2 py-0.5 rounded border border-rose-300 text-rose-800"
                    >
                      {interview.nome} {interview.cognome} ({interview.days_since_interview}g)
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Search - Compatto */}
          <div className="mb-3 flex-shrink-0">
            <div className="flex items-center gap-2 max-w-2xl">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cerca per nome, codice, telefono o commerciale..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-9"
                  data-testid="search-input"
                />
              </div>
              {searchTerm && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearSearchFilter}
                  data-testid="btn-tutte-interviste"
                  className="h-9 border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  <Users className="w-4 h-4 mr-1" />
                  Tutte Interviste
                </Button>
              )}
            </div>
          </div>

          {/* Tab Filtro Stati */}
          <div className="mb-4 flex-shrink-0">
            <div className="flex flex-wrap gap-2">
              {STATI_FILTRO.map((stato) => (
                <Button
                  key={stato.value}
                  variant={statusFilter === stato.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter(stato.value)}
                  className={`
                    ${statusFilter === stato.value 
                      ? stato.value === "all" 
                        ? "bg-slate-700 hover:bg-slate-800" 
                        : stato.value === "Da Elaborare"
                          ? "bg-gray-600 hover:bg-gray-700"
                          : stato.value === "In Elaborazione"
                            ? "bg-yellow-600 hover:bg-yellow-700"
                            : stato.value === "Elaborato"
                              ? "bg-green-600 hover:bg-green-700"
                              : stato.value === "Preso App in Showroom"
                                ? "bg-blue-600 hover:bg-blue-700"
                                : stato.value === "Consegnato"
                                  ? "bg-emerald-600 hover:bg-emerald-700"
                                  : stato.value === "consegnati_inesitati"
                                    ? "bg-amber-500 hover:bg-amber-600"
                                    : stato.value === "inconcludenti"
                                      ? "bg-red-600 hover:bg-red-700"
                                      : "bg-slate-600 hover:bg-slate-700"
                      : "hover:bg-slate-100"
                    }
                  `}
                  data-testid={`filter-${stato.value}`}
                >
                  {stato.value === "consegnati_inesitati" && statusCounts["consegnati_inesitati"] > 0 && (
                    <Clock className="w-4 h-4 mr-1 animate-pulse" />
                  )}
                  {stato.value === "inconcludenti" && statusCounts["inconcludenti"] > 0 && (
                    <AlertTriangle className="w-4 h-4 mr-1 animate-pulse" />
                  )}
                  {stato.label}
                  <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${
                    statusFilter === stato.value 
                      ? "bg-white/20 text-white" 
                      : "bg-slate-200 text-slate-700"
                  }`}>
                    {statusCounts[stato.value] || 0}
                  </span>
                </Button>
              ))}
            </div>
          </div>

          {/* Filtri Marketing - Categoria Merceologica */}
          <div className="mb-4 flex-shrink-0">
            <p className="text-xs text-slate-500 mb-2 font-medium">Filtri Marketing</p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={categoriaFilter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setCategoriaFilter("all")}
                className={categoriaFilter === "all" ? "bg-purple-600 hover:bg-purple-700" : "hover:bg-purple-50 border-purple-200"}
                data-testid="filter-cat-all"
              >
                Tutte le Categorie
              </Button>
              <Button
                variant={categoriaFilter === "solo_porte" ? "default" : "outline"}
                size="sm"
                onClick={() => setCategoriaFilter("solo_porte")}
                className={categoriaFilter === "solo_porte" ? "bg-orange-500 hover:bg-orange-600" : "hover:bg-orange-50 border-orange-200 text-orange-700"}
                data-testid="filter-cat-porte"
              >
                Solo Porte
                <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${
                  categoriaFilter === "solo_porte" ? "bg-white/20 text-white" : "bg-orange-100 text-orange-700"
                }`}>
                  {interviews.filter(i => i.categoria_merceologica === "Porte").length}
                </span>
              </Button>
              <Button
                variant={categoriaFilter === "porte_e_infissi" ? "default" : "outline"}
                size="sm"
                onClick={() => setCategoriaFilter("porte_e_infissi")}
                className={categoriaFilter === "porte_e_infissi" ? "bg-teal-500 hover:bg-teal-600" : "hover:bg-teal-50 border-teal-200 text-teal-700"}
                data-testid="filter-cat-porte-infissi"
              >
                Porte e Infissi
                <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${
                  categoriaFilter === "porte_e_infissi" ? "bg-white/20 text-white" : "bg-teal-100 text-teal-700"
                }`}>
                  {interviews.filter(i => i.categoria_merceologica === "Infissi e Porte").length}
                </span>
              </Button>
              <Button
                variant={categoriaFilter === "solo_infissi" ? "default" : "outline"}
                size="sm"
                onClick={() => setCategoriaFilter("solo_infissi")}
                className={categoriaFilter === "solo_infissi" ? "bg-sky-500 hover:bg-sky-600" : "hover:bg-sky-50 border-sky-200 text-sky-700"}
                data-testid="filter-cat-infissi"
              >
                Solo Infissi
                <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${
                  categoriaFilter === "solo_infissi" ? "bg-white/20 text-white" : "bg-sky-100 text-sky-700"
                }`}>
                  {interviews.filter(i => i.categoria_merceologica === "Infissi").length}
                </span>
              </Button>
              <Button
                variant={categoriaFilter === "fuori_budget" ? "default" : "outline"}
                size="sm"
                onClick={() => setCategoriaFilter("fuori_budget")}
                className={categoriaFilter === "fuori_budget" ? "bg-rose-500 hover:bg-rose-600" : "hover:bg-rose-50 border-rose-200 text-rose-700"}
                data-testid="filter-cat-fuori-budget"
              >
                Fuori Budget
                <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${
                  categoriaFilter === "fuori_budget" ? "bg-white/20 text-white" : "bg-rose-100 text-rose-700"
                }`}>
                  {interviews.filter(i => i.stato === "Fuori Budget").length}
                </span>
              </Button>
            </div>
          </div>

          {/* Table con scroll interno */}
          {loading ? (
            <SkeletonTable rows={8} cols={6} />
          ) : (
            <div className="flex-1 overflow-hidden bg-white rounded-lg border border-slate-200 shadow-sm">
              <div className="overflow-auto h-full" data-testid="interviews-table">
                <table className="w-full data-table">
                  <thead className="sticky top-0 bg-slate-50 z-10">
                    <tr>
                      <th className="min-w-[70px] px-2 py-2 text-xs">Codice</th>
                      <th className="min-w-[150px] px-2 py-2 text-xs">Cliente</th>
                      <th className="min-w-[100px] px-2 py-2 text-xs">Telefono</th>
                      <th className="min-w-[80px] px-2 py-2 text-xs">Data</th>
                      <th className="min-w-[100px] px-2 py-2 text-xs">Priorità</th>
                      <th className="min-w-[50px] px-2 py-2 text-xs">Gg</th>
                      <th className="min-w-[160px] px-2 py-2 text-xs">Stato</th>
                      <th className="min-w-[120px] px-2 py-2 text-xs">Esito</th>
                      <th className="min-w-[80px] px-2 py-2 text-xs">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                  {filteredInterviews.map((interview) => {
                    const isWaiting = interview.stato === "Da Elaborare" || interview.stato === "In Elaborazione";
                    const hasEsito = interview.esito === "vinto" || interview.esito === "perso";
                    const rowStyle = getRowStyle(interview);
                    return (
                      <tr key={interview.id} data-testid={`interview-row-${interview.id}`} className={`${getRowClass(interview)} text-sm`} style={rowStyle}>
                        <td style={rowStyle} className="px-2 py-1.5">
                          <div className="flex items-center gap-1">
                            {!hasEsito && isWaiting && interview.days_since_interview > 5 && (
                              <div className="w-2 h-2 bg-red-600 rounded-full animate-pulse" />
                            )}
                            {!hasEsito && isWaiting && interview.days_since_interview > 3 && interview.days_since_interview <= 5 && (
                              <div className="w-2 h-2 bg-rose-500 rounded-full animate-pulse" />
                            )}
                            <span className="font-mono text-xs">{interview.codice}</span>
                          </div>
                        </td>
                        <td style={rowStyle} className={`px-2 py-1.5 font-medium text-xs ${
                          interview.esito === "vinto" ? "text-emerald-700" :
                          interview.esito === "perso" ? "text-violet-700" :
                          isWaiting && interview.days_since_interview > 3 ? "text-rose-700" : ""
                        }`}>
                          {interview.nome} {interview.cognome}
                        </td>
                        <td style={rowStyle} className="px-2 py-1.5 text-xs">{interview.telefono}</td>
                        <td style={rowStyle} className="px-2 py-1.5 text-xs">{convertToItalianFormat(interview.data_intervista)}</td>
                        <td style={rowStyle} className="px-2 py-1.5 text-xs">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            interview.priorita === "Urgente 1 g" 
                              ? "bg-red-100 text-red-700" 
                              : interview.priorita === "Lento 6-10 gg"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-slate-100 text-slate-700"
                          }`}>
                            {interview.priorita || "Ordinario 3-5 gg"}
                          </span>
                        </td>
                        <td style={rowStyle} className="px-2 py-1.5">
                          {isWaiting && !hasEsito && (
                            <span className={`text-xs font-medium ${
                              interview.days_since_interview > 5 
                                ? "text-red-700 bg-red-100 px-1.5 py-0.5 rounded" 
                                : interview.days_since_interview > 3 
                                  ? "text-rose-600 bg-rose-100 px-1.5 py-0.5 rounded" 
                                  : "text-slate-500"
                            }`}>
                              {interview.days_since_interview}
                            </span>
                          )}
                        </td>
                        <td style={rowStyle} className="px-2 py-1.5">
                          <Select
                            value={interview.stato}
                            onValueChange={(value) => handleStatusChange(interview.id, value)}
                          >
                            <SelectTrigger className={`w-36 h-7 text-xs ${getStatusColor(interview.stato)}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATI.map((stato) => (
                                <SelectItem key={stato} value={stato} className="text-xs">
                                  {stato}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td style={rowStyle} className="px-2 py-1.5">
                          <div className="flex gap-0.5">
                            <Button
                              variant={interview.esito === "vinto" ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleEsitoClick(interview.id, "vinto", interview.esito)}
                              className={`h-7 w-7 p-0 ${interview.esito === "vinto" ? "bg-emerald-600 hover:bg-emerald-700" : "hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-300"}`}
                              title={interview.esito === "vinto" ? "Doppio clic per rimuovere" : "Vinto"}
                              data-testid={`vinto-btn-${interview.id}`}
                            >
                              <Trophy className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant={interview.esito === "perso" ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleEsitoClick(interview.id, "perso", interview.esito)}
                              className={`h-7 w-7 p-0 ${interview.esito === "perso" ? "bg-violet-600 hover:bg-violet-700" : "hover:bg-violet-50 hover:text-violet-600 hover:border-violet-300"}`}
                              title={interview.esito === "perso" ? "Doppio clic per rimuovere" : "Perso"}
                              data-testid={`perso-btn-${interview.id}`}
                            >
                              <Skull className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant={interview.esito === "sparito" ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleEsitoClick(interview.id, "sparito", interview.esito)}
                              className={`h-7 w-7 p-0 ${interview.esito === "sparito" ? "bg-slate-600 hover:bg-slate-700" : "hover:bg-slate-50 hover:text-slate-600 hover:border-slate-300"}`}
                              title={interview.esito === "sparito" ? "Doppio clic per rimuovere" : "Sparito"}
                              data-testid={`sparito-btn-${interview.id}`}
                            >
                              <Ghost className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                        <td style={rowStyle} className="px-2 py-1.5">
                          <div className="flex gap-0.5 justify-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(interview)}
                              className="h-7 w-7 p-0"
                              data-testid={`edit-btn-${interview.id}`}
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-btn-${interview.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Sei sicuro di voler eliminare l&apos;intervista di {interview.nome} {interview.cognome}?
                                    Questa azione non può essere annullata.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(interview.id)}
                                    className="bg-rose-600 hover:bg-rose-700"
                                  >
                                    Elimina
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredInterviews.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  {searchTerm ? "Nessun risultato trovato" : "Nessuna intervista presente"}
                </div>
              )}
            </div>
            </div>
          )}
        </div>
    </PageLayout>
  );
}
